import {
  OperationBriefBlocks,
  OperationBriefForm,
} from 'models/OperationRequest';

export const OPS_BRANDS = [
  'Nespresso',
  'Nike',
  'Motorola',
  'DAC',
  'Tienda Perfecta',
] as const;

export const OPS_CAMPAIGNS: Record<string, string[]> = {
  Nespresso: [
    'Lanzamiento Vertuo Pop',
    'Sampling Vertuo — malls',
    'Navidad 2026',
  ],
  Nike: ['Run Club — activación tiendas', 'Back to school', 'Air Max Day'],
  Motorola: ['Edge 60 — demo en sala', 'Razr — experiencia retail'],
  DAC: ['Degustación línea premium', 'Fiestas Patrias'],
  'Tienda Perfecta': ['Reposición fin de mes', 'Auditoría de góndola'],
};

export const OPS_KAMS = [
  'Luis Muñoz',
  'Astrid Coy',
  'Alika Zeran',
  'Camila Rojas',
  'Tomás Vergara',
];

export const OPS_PROFILES = [
  'Anfitrión/a',
  'Promotor/a',
  'Degustador/a',
  'Mercaderista',
  'Brand Ambassador',
];

export const OPS_UNIFORMS = [
  {
    id: 'u1',
    label: 'Polera negra + jeans',
    note: 'La trae el postulante',
  },
  {
    id: 'u2',
    label: 'Uniforme de marca',
    note: 'Se entrega en capacitación',
  },
  {
    id: 'u3',
    label: 'Formal / ejecutivo',
    note: 'Camisa blanca y pantalón negro',
  },
  {
    id: 'u4',
    label: 'Casual sport',
    note: 'Según brief de la marca',
  },
];

export const OPS_GOALS = [
  'Degustación',
  'Venta asistida',
  'Sampling',
  'Levantamiento de datos',
  'Activación BTL',
  'Reposición / mercadeo',
];

export interface OpsWeekDayDates {
  lun: string;
  mar: string;
  mie: string;
  jue: string;
  vie: string;
  sab: string;
  dom: string;
}

export interface OpsWeekOption {
  value: string;
  label: string;
  /** Días desde hoy hasta el lunes de esa semana (0 = semana actual). */
  startsIn: number;
  startDate: string;
  dates: OpsWeekDayDates;
}

const MONTHS_SHORT_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const;

const MONTH_INDEX: Record<string, number> = {
  ene: 0,
  feb: 1,
  mar: 2,
  abr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  ago: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dic: 11,
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/** Lunes de la semana que contiene `date` (semana lun–dom). */
const startOfWeekMonday = (date: Date) => {
  const day = startOfDay(date);
  const weekday = day.getDay(); // 0 = domingo
  const offset = weekday === 0 ? -6 : 1 - weekday;
  return addDays(day, offset);
};

const formatDayMonth = (date: Date) =>
  `${date.getDate()} ${MONTHS_SHORT_ES[date.getMonth()]}`;

const formatWeekRange = (monday: Date, sunday: Date) => {
  const year = sunday.getFullYear();
  if (monday.getMonth() === sunday.getMonth()) {
    return `${monday.getDate()}-${sunday.getDate()} ${
      MONTHS_SHORT_ES[monday.getMonth()]
    } ${year}`;
  }
  return `${monday.getDate()} ${
    MONTHS_SHORT_ES[monday.getMonth()]
  } - ${sunday.getDate()} ${MONTHS_SHORT_ES[sunday.getMonth()]} ${year}`;
};

const monthTokenToIndex = (token: string): number | undefined =>
  MONTH_INDEX[token.toLowerCase().slice(0, 3)];

const buildLocalDate = (
  day: number,
  monthToken: string,
  year: number
): Date | null => {
  const month = monthTokenToIndex(monthToken);
  if (month === undefined || !Number.isFinite(day) || !Number.isFinite(year)) {
    return null;
  }
  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return startOfDay(date);
};

/**
 * Parsea el lunes de una etiqueta de semana del brief.
 * Soporta:
 * - "22-28 sep 2026"
 * - "28 sep - 4 oct 2026"
 */
export const parseOpsWeekStartDate = (week: string): Date | null => {
  const trimmed = (week || '').trim();
  if (!trimmed) return null;

  const sameMonth = trimmed.match(
    /^(\d{1,2})-\d{1,2}\s+([a-záéíóúñ]+)\s+(\d{4})$/i
  );
  if (sameMonth) {
    return buildLocalDate(
      Number(sameMonth[1]),
      sameMonth[2],
      Number(sameMonth[3])
    );
  }

  const crossMonth = trimmed.match(
    /^(\d{1,2})\s+([a-záéíóúñ]+)\s*-\s*\d{1,2}\s+[a-záéíóúñ]+\s+(\d{4})$/i
  );
  if (crossMonth) {
    return buildLocalDate(
      Number(crossMonth[1]),
      crossMonth[2],
      Number(crossMonth[3])
    );
  }

  return null;
};

/**
 * Días hasta el lunes de la semana del requerimiento.
 * Negativo = ya empezó / venció. 99 = semana no parseable.
 */
export const computeStartsInFromWeek = (
  week: string,
  startDateIso?: string | null
): number => {
  let monday: Date | null = null;

  if (startDateIso && /^\d{4}-\d{2}-\d{2}/.test(startDateIso)) {
    const [y, mo, d] = startDateIso.slice(0, 10).split('-').map(Number);
    if (y && mo && d) {
      monday = startOfDay(new Date(y, mo - 1, d));
    }
  }

  if (!monday) {
    monday = parseOpsWeekStartDate(week);
  }

  if (!monday) return 99;

  const today = startOfDay(new Date());
  return Math.round((monday.getTime() - today.getTime()) / 86_400_000);
};

/**
 * Semana actual (lun–dom) + `ahead` semanas hacia adelante.
 * Por defecto: actual + 3 = 4 opciones.
 */
export const buildOpsWeeks = (
  from: Date = new Date(),
  ahead = 3
): OpsWeekOption[] => {
  const today = startOfDay(from);
  const currentMonday = startOfWeekMonday(from);
  const total = ahead + 1;
  const weeks: OpsWeekOption[] = [];

  for (let i = 0; i < total; i += 1) {
    const monday = addDays(currentMonday, i * 7);
    const sunday = addDays(monday, 6);
    const range = formatWeekRange(monday, sunday);
    const startsIn = Math.max(
      0,
      Math.round((monday.getTime() - today.getTime()) / 86_400_000)
    );

    weeks.push({
      value: range,
      label: `Semana ${range}`,
      startsIn,
      startDate: startsIn === 0 ? 'Hoy' : formatDayMonth(monday),
      dates: {
        lun: formatDayMonth(monday),
        mar: formatDayMonth(addDays(monday, 1)),
        mie: formatDayMonth(addDays(monday, 2)),
        jue: formatDayMonth(addDays(monday, 3)),
        vie: formatDayMonth(addDays(monday, 4)),
        sab: formatDayMonth(addDays(monday, 5)),
        dom: formatDayMonth(sunday),
      },
    });
  }

  return weeks;
};

/** Semana actual + 3 siguientes. Preferí llamar esto al momento de uso. */
export const getOpsWeeks = () => buildOpsWeeks();

export const OPS_WEEK_DAYS = [
  { key: 'lun', label: 'Lunes', short: 'Lun' },
  { key: 'mar', label: 'Martes', short: 'Mar' },
  { key: 'mie', label: 'Miércoles', short: 'Mié' },
  { key: 'jue', label: 'Jueves', short: 'Jue' },
  { key: 'vie', label: 'Viernes', short: 'Vie' },
  { key: 'sab', label: 'Sábado', short: 'Sáb' },
  { key: 'dom', label: 'Domingo', short: 'Dom' },
];

export const OPS_DEFAULT_BLOCKS: OperationBriefBlocks = {
  am: { from: '10:00', to: '14:00' },
  pm: { from: '14:00', to: '18:00' },
  full: { from: '10:00', to: '18:00' },
};

export const OPS_DEFAULT_BRIEF: OperationBriefForm = {
  clientId: null,
  campaignId: null,
  client: '',
  type: 'contrato',
  campaign: '',
  week: getOpsWeeks()[0]?.value || '',
  profile: '',
  uniform: '',
  goal: '',
  description: '',
  kam: '',
  rate: 32000,
  blocks: { ...OPS_DEFAULT_BLOCKS },
};

export const OPS_TIME_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let h = 8; h <= 23; h += 1) {
    for (const m of ['00', '30']) {
      if (h === 23 && m === '30') continue;
      out.push(`${String(h).padStart(2, '0')}:${m}`);
    }
  }
  return out;
})();

export const createEmptyTurn = (
  over: Partial<{
    storeId: number | null;
    day: string;
    block: 'am' | 'pm' | 'full';
    from: string;
    to: string;
    people: number;
    notes: string;
  }> = {}
) => ({
  uid: Math.random().toString(36).slice(2, 9),
  storeId: over.storeId ?? null,
  day: over.day ?? 'sab',
  block: over.block,
  from: over.from ?? '14:00',
  to: over.to ?? '18:00',
  people: over.people ?? 2,
  notes: over.notes ?? '',
});

export const planCellsToTurns = (
  selectedStoreIds: number[],
  cells: Record<string, Partial<Record<'am' | 'pm' | 'full', number>>>,
  brief: {
    blocks: {
      am: { from: string; to: string };
      pm: { from: string; to: string };
      full: { from: string; to: string };
    };
  }
) => {
  const turns: ReturnType<typeof createEmptyTurn>[] = [];
  selectedStoreIds.forEach((storeId) => {
    OPS_WEEK_DAYS.forEach((day) => {
      const key = `${storeId}:${day.key}`;
      const cell = cells[key] || {};
      (['am', 'pm', 'full'] as const).forEach((block) => {
        const people = cell[block];
        if (!people || people <= 0) return;
        turns.push(
          createEmptyTurn({
            storeId,
            day: day.key,
            block,
            from: brief.blocks[block].from,
            to: brief.blocks[block].to,
            people,
          })
        );
      });
    });
  });
  return turns;
};

export const turnoHours = (from: string, to: string) => {
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  return Math.max(0, (th * 60 + tm - fh * 60 - fm) / 60);
};

export const isTurnValid = (t: {
  storeId: number | null;
  from: string;
  to: string;
  people: number;
}) => !!t.storeId && turnoHours(t.from, t.to) > 0 && t.people > 0;

export const formatClp = (n: number) =>
  `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`;
